import { useState, useMemo } from "react";
import ImageUploadContainer from "../../Components/FileUplaodContainer/ImageUploadContainer";
import TertiaryButton from "../../Components/Buttons/TertiaryButton";
import SecondaryButton from "../../Components/Buttons/SecondaryButton";
import fileToBase64 from "../../Checks/utils/fileToBase64";

function UploadNewDocumentModal({
  open,
  onClose,
  checkType, // "standard" | "enhanced" (lowercase preferred)
  sessionId, // existing veriff sessionId
  onDone, // callback after success, gets latest check if available
  autoSubmit = true, // also call /:sessionId/submit after uploads
  setIsPollingDecision,
}) {
  const [images, setImages] = useState({ front: null, back: null });
  const [imageErrors, setImageErrors] = useState({ front: "", back: "" });
  const [submitting, setSubmitting] = useState(false);

  const endpoint = useMemo(() => {
    if (!checkType || !sessionId) return "";
    const t = (checkType || "").toLowerCase();
    const base = process.env.REACT_APP_AML_SERVER || "";
    return t === "enhanced"
      ? `${base}get-idv-decision/${encodeURIComponent(sessionId)}`
      : `${base}get-docOnly-decision/${encodeURIComponent(sessionId)}`;
  }, [checkType, sessionId]);

  if (!open) return null;

  const AML_SERVER = (process.env.REACT_APP_AML_SERVER || "").replace(
    /\/$/,
    ""
  );

  const onFrontImageChange = (file) => {
    setImages((p) => ({ ...p, front: file || null }));
    setImageErrors((p) => ({ ...p, front: "" }));
  };
  const onBackImageChange = (file) => {
    setImages((p) => ({ ...p, back: file || null }));
    setImageErrors((p) => ({ ...p, back: "" }));
  };

  const onFrontImageError = (msg) =>
    setImageErrors((p) => ({ ...p, front: msg || "Upload failed." }));
  const onBackImageError = (msg) =>
    setImageErrors((p) => ({ ...p, back: msg || "Upload failed." }));

  const postImage = async (context, base64) => {
    // HITS: /standard-check/:sessionId/media  OR  /enhanced-check/:sessionId/media
    const url = `${AML_SERVER}/${checkType}-check/${encodeURIComponent(
      sessionId
    )}/media`;

    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        context, // "document-front" | "document-back"
        timestamp: new Date().toISOString(),
        content: base64, // may include data URL; backend strips prefix
      }),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(t || `Failed to upload ${context}`);
    }
    return res.json();
  };

  const submitSession = async () => {
    const url = `${AML_SERVER}/${checkType}-check/${encodeURIComponent(
      sessionId
    )}/submit`;
    const r = await fetch(url, {
      method: "POST",
      headers: { Accept: "application/json" },
    });
    // We don't hard-fail UI if submit returns 4xx — log and continue
    if (!r.ok) {
      // console.warn("Submit failed:", await r.text())
    }
  };

  // Small sleep helper for polling
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Fetch updated decision once
  const fetchUpdatedDecision = async () => {
    const url = endpoint;

    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(t || "Failed to fetch updated decision");
    }

    return res.json(); // { decision, verification, attempts, ... } depending on backend
  };

  //  Poll every 10s for 1 minute
  const startDecisionPolling = async () => {
    const POLL_INTERVAL_MS = 5_000; // 5 seconds
    const MAX_DURATION_MS = 10_000; // 10 seconds
    const startedAt = Date.now();

    while (Date.now() - startedAt < MAX_DURATION_MS) {
      try {
        const updated = await fetchUpdatedDecision();
        // Let parent re-read / merge this into its own state
        onDone?.(updated);

        const status =
          updated?.verification?.status || updated?.decision?.status || "";

        // If status has moved away from resubmission_requested, we can stop early
        if (
          typeof status === "string" &&
          status.toLowerCase() !== "resubmission_requested"
        ) {
          break;
        }
      } catch (e) {
        // If fetch fails we just try again on next tick
        // console.warn("Decision poll failed", e);
      }

      await sleep(POLL_INTERVAL_MS);
    }
  };

  
  const handleSubmit = async () => {
    if (submitting) return;

    // Require at least one side
    if (!images.front && !images.back) {
      setImageErrors((p) => ({
        ...p,
        front: "Please upload at least one image.",
      }));
      return;
    }

    try {
      setSubmitting(true);

      const frontB64 = images.front ? await fileToBase64(images.front) : null;
      const backB64 = images.back ? await fileToBase64(images.back) : null;

      if (frontB64) await postImage("document-front", frontB64);
      if (backB64) await postImage("document-back", backB64);

      if (autoSubmit) await submitSession();

      // start polling state
      setIsPollingDecision?.(true);

      // optional initial fetch before closing modal
      try {
        const firstSnapshot = await fetchUpdatedDecision();
        onDone?.(firstSnapshot);
      } catch {
        // ignore, polling will try again
      }

      // close modal for the user
      onClose?.();

      // fire-and-forget polling; when it finishes, clear polling flag
      startDecisionPolling()
        .catch(() => {})
        .finally(() => setIsPollingDecision?.(false));
    } catch (err) {
      setImageErrors((p) => ({
        ...p,
        front: err.message || "Upload failed.",
      }));
      setIsPollingDecision?.(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="Resubmit-Overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Upload new document"
    >
      <div className="Resubmit-Modal">
        <div className="Check-Section">
          <div className="AML-Text-Input">Identification Document</div>
          <div className="AML-Text-Body">Resubmission Required</div>

          <div className="Identification-Document-Container">
            <ImageUploadContainer
              id="upload-front"
              label="Front Side"
              onChange={onFrontImageChange}
              onError={onFrontImageError}
              errorMessage={imageErrors.front}
            />
            <ImageUploadContainer
              id="upload-back"
              label="Back Side"
              onChange={onBackImageChange}
              onError={onBackImageError}
              errorMessage={imageErrors.back}
            />
          </div>

          <div className="Upload-Buttons-Row">
            <SecondaryButton
              label={submitting ? "Submitting..." : "Submit"}
              onClick={handleSubmit}
              isDisabled={submitting}
            />
            <TertiaryButton buttonText="Cancel" onClick={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadNewDocumentModal;
